import type {FC, ReactNode} from 'react';
import moment from 'moment';
import {
  Box,
  Icon,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@wellstone-solutions/web';
// eslint-disable-next-line
// @ts-ignore - legacy Flow component
import TutorialDiv from 'components/tutorial/tutorialdiv';
import {InitialsAvatar} from 'components/InitialsAvatar';
import {RecoveryCounter} from '../RecoveryCounter';
import {InfoField} from './InfoField';
import {AttributePill} from './AttributePill';
import {StatusChip} from './StatusChip';
import {CheckinStatusChip} from './CheckinStatusChip';
import {GlobalIdFooter} from './GlobalIdFooter';
import {headerPalette} from './palette';
import {
  CHIPS_MIN_WIDTH,
  HEADER_CARD_CONTAINER,
  HEADER_CARD_WIDE,
} from './containers';

interface MemberHeaderCardProps {
  name: string;
  userId: string;
  globalId: string;
  birthdate?: string | null;
  email?: string | null;
  genderLabel: string;
  identifiesAsLabel: string;
  lastCheckIn?: string | null;
  lastAppOpen?: string | null;
  // Wired legacy elements rendered by the parent (RBAC, stores, modals)
  editButton?: ReactNode;
  chatToggle?: ReactNode;
  // Current value of the chat toggle; drives the help tooltip copy
  chatEnabled?: boolean;
  // Shown in place of the toggle when the viewer can't change chat access
  chatFallback?: ReactNode;
  ehrConnect?: ReactNode;
  resendInvite?: ReactNode;
  isTutorialTarget?: boolean;
}

export const CHAT_ENABLED_HELP_TEXT =
  'Turning this off disables messaging access in Companion between members.';
export const CHAT_DISABLED_HELP_TEXT =
  'Turning this on enables messaging access in Companion between members.';

export const MemberHeaderCard: FC<MemberHeaderCardProps> = ({
  name,
  userId,
  globalId,
  birthdate,
  email,
  genderLabel,
  identifiesAsLabel,
  lastCheckIn,
  lastAppOpen,
  editButton,
  chatToggle,
  chatEnabled,
  chatFallback,
  ehrConnect,
  resendInvite,
  isTutorialTarget,
}) => {
  const age = birthdate ? moment().diff(birthdate, 'years') : null;

  return (
    <Paper
      sx={
        isTutorialTarget
          ? {...styles.card, ...styles.cardElevated}
          : styles.card
      }>
      <Box sx={styles.headerRow}>
        <Box sx={{gridArea: 'avatar'}}>
          <InitialsAvatar
            name={name}
            size={48}
            sx={{
              bgcolor: headerPalette.violetSoft,
              color: headerPalette.violet,
              fontWeight: 600,
            }}
          />
        </Box>
        <Box sx={{gridArea: 'name', minWidth: 0}}>
          <Typography sx={styles.name}>{name}</Typography>
          {ehrConnect}
        </Box>
        <Box sx={{gridArea: 'button', justifySelf: 'end'}}>{editButton}</Box>
      </Box>

      <Box sx={styles.columns}>
        <Box sx={styles.leftColumn}>
          {chatToggle ? (
            <Stack direction="row" alignItems="center" spacing={1}>
              {chatToggle}
              <Typography sx={styles.chatLabel}>Enable Chat</Typography>
              <Tooltip
                title={
                  chatEnabled ? CHAT_ENABLED_HELP_TEXT : CHAT_DISABLED_HELP_TEXT
                }>
                {/* Native span: web's Box doesn't forward refs, which
                    prevents the Tooltip from anchoring and opening */}
                <span
                  style={{display: 'inline-flex'}}
                  data-testid="chat-help-icon">
                  <Icon
                    name="HelpCircle"
                    size={17}
                    color={headerPalette.textDisabled}
                  />
                </span>
              </Tooltip>
            </Stack>
          ) : (
            chatFallback
          )}
          {resendInvite}

          <Box sx={styles.infoGrid}>
            <InfoField label="Date of birth">
              {birthdate ? (
                <Typography sx={styles.infoValue}>
                  {moment(birthdate).format('MMM D, YYYY')}
                  {age !== null && (
                    <Typography component="span" sx={styles.infoValueMuted}>
                      {' '}
                      · Age {age}
                    </Typography>
                  )}
                </Typography>
              ) : (
                '—'
              )}
            </InfoField>
            <InfoField label="Email" truncate>
              {email || '—'}
            </InfoField>
            <InfoField label="Sex">
              <AttributePill label={genderLabel} />
            </InfoField>
            <InfoField label="Identifies as">
              <AttributePill label={identifiesAsLabel} />
            </InfoField>
          </Box>
        </Box>

        <Box sx={styles.rightColumn}>
          <Stack direction="row" flexWrap="wrap" sx={{gap: 1, minWidth: 0}}>
            <TutorialDiv id="since-checkin-counter">
              <CheckinStatusChip lastCheckIn={lastCheckIn} />
            </TutorialDiv>
            {lastAppOpen && (
              <StatusChip
                icon="SmartphoneDevice"
                label={`Last app open · ${moment(lastAppOpen).format(
                  'MM/DD/YY, h:mm A',
                )}`}
                background={headerPalette.neutralBg}
                foreground={headerPalette.textSecondary}
                showTitle
              />
            )}
          </Stack>
          <RecoveryCounter userId={userId} memberName={name} />
        </Box>
      </Box>

      <GlobalIdFooter globalId={globalId} />
    </Paper>
  );
};

const styles = {
  cardElevated: {
    position: 'relative',
    // Clears the tutorial's page cover at 400
    zIndex: 501,
  },
  card: {
    borderRadius: 2,
    containerName: HEADER_CARD_CONTAINER,
    containerType: 'inline-size',
    marginBottom: 4,
    padding: 3,
  },
  headerRow: {
    alignItems: 'center',
    columnGap: 2,
    display: 'grid',
    gridTemplateAreas: '"avatar name button"',
    gridTemplateColumns: '48px 1fr auto',
    marginBottom: '18px',
  },
  columns: {
    display: 'flex',
    flexDirection: 'column',
    gap: 3,
    [HEADER_CARD_WIDE]: {
      alignItems: 'flex-start',
      flexDirection: 'row',
      gap: '30px',
    },
  },
  name: {
    color: headerPalette.textPrimary,
    fontSize: 28,
    fontWeight: 700,
    lineHeight: 1.2,
    overflowWrap: 'anywhere',
  },
  leftColumn: {
    minWidth: 0,
    width: '100%',
    // Does not grow, or the label/value pairs spread out
    [HEADER_CARD_WIDE]: {flex: '0 1 530px'},
  },
  rightColumn: {
    minWidth: 0,
    width: '100%',
    // Takes the slack on wider cards; the floor keeps both chips on one line
    [HEADER_CARD_WIDE]: {
      flex: '1 1 440px',
      minWidth: `${CHIPS_MIN_WIDTH}px`,
      width: 'auto',
    },
  },
  chatLabel: {
    color: headerPalette.textPrimary,
    fontSize: 15,
    fontWeight: 500,
  },
  infoGrid: {
    borderTop: `1px solid ${headerPalette.divider}`,
    columnGap: '30px',
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    marginTop: '18px',
    paddingTop: '23px',
    rowGap: '18px',
    // Even halves are too tight for the email in a 530px column; the cap stops
    // a long date starving it
    [HEADER_CARD_WIDE]: {
      gridTemplateColumns: 'fit-content(40%) minmax(0, 1fr)',
    },
  },
  infoValue: {
    color: headerPalette.textPrimary,
    fontSize: 16,
  },
  infoValueMuted: {
    color: headerPalette.textSecondary,
    fontSize: 16,
    // Never split "Age" from the number
    whiteSpace: 'nowrap',
  },
};
